import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetRevenueVsExpensesInsightsUseCase } from "../../domain/use-cases/GetRevenueVsExpensesInsightsUseCase";

const repository = new ClientOverviewRepositoryImpl();
const useCase = new GetRevenueVsExpensesInsightsUseCase(repository);

export interface RevenueVsExpensesInsightsData {
  title: string;
  body: string;
  sentiment: "positive" | "neutral" | "negative";
  loading: boolean;
  error: string | null;
}

export interface UseRevenueVsExpensesInsightsOptions {
  /** When false, the insights API is not called. Set to true only after twelve-month trend has loaded. */
  enabled?: boolean;
}

export function useRevenueVsExpensesInsights(
  options?: UseRevenueVsExpensesInsightsOptions,
): RevenueVsExpensesInsightsData {
  const enabled = options?.enabled ?? true;

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sentiment, setSentiment] = useState<"positive" | "neutral" | "negative">("neutral");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!enabled) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const entity = await useCase.execute();
      setTitle(entity.title);
      setBody(entity.body);
      setSentiment(entity.sentiment);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load revenue vs expenses insights",
      );
      setTitle("");
      setBody("");
      setSentiment("neutral");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchInsights();
  }, [enabled, fetchInsights]);

  return { title, body, sentiment, loading, error };
}
